// Shared cart utilities — used by product-card.js, product-page.js, and
// any future surface that needs to add to cart, animate the cart badge,
// or run a button through the loading-state cycle.
//
// Exposed on window.toyCart so dependent scripts can use it without
// imports (themes don't bundle ES modules). Guard with idempotency so
// it's safe to load multiple times.

(function () {
  if (window.toyCart) return;

  const ADDED_FEEDBACK_MS = 1400;
  const ERROR_FEEDBACK_MS = 1800;
  const LABEL_ADDING = 'Adding…';
  const LABEL_ADDED = 'Added ✓';
  const LABEL_RETRY = 'Try again';

  // ===== Money formatting =====
  function formatMoney(cents) {
    const num = parseFloat(cents) / 100;
    if (Number.isNaN(num)) return '';
    const locale = (window.Shopify && window.Shopify.locale) || 'en-PK';
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'PKR';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `Rs ${num.toLocaleString('en-PK')}`;
    }
  }

  // ===== Section Rendering swap =====
  // Replace a live container's innerHTML with the matching element from freshly
  // rendered section HTML (Section Rendering API). Used by the cart page and the
  // cart drawer so the swap logic lives in one place. `root` scopes the live
  // lookup (pass the drawer so it doesn't grab the cart page's region, or omit
  // for document). Taking only the inner markup keeps the section's <script> /
  // {% stylesheet %} (which sit outside the wrapper) from being re-injected.
  function renderSection(selector, html, root) {
    const scope = root || document;
    const live = scope.querySelector(selector);
    if (!live || !html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(selector);
    if (!fresh) return false;
    live.innerHTML = fresh.innerHTML;
    return true;
  }

  // Dim a cart line + disable its buttons while a request is in flight.
  function setLineBusy(lineEl, busy) {
    lineEl.classList.toggle('cart-item--busy', busy);
    lineEl.querySelectorAll('button').forEach((btn) => {
      btn.disabled = busy;
    });
  }

  // ===== Cart badge in the header =====
  function updateCartBadge(count) {
    const cartBag = document.querySelector('.cart-bag');
    if (!cartBag) return;

    let badge = cartBag.querySelector('.cart-bag__badge');
    const display = count > 99 ? '99+' : String(count);

    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cart-bag__badge';
        badge.setAttribute('aria-hidden', 'true');
        cartBag.appendChild(badge);
      }
      badge.textContent = display;
      // Mirror the mobile app's withSequence(withTiming(1.3, 140), withSpring(1)).
      // Spring approximated via overshoot keyframes — settles at 1 with two
      // small bounces.
      badge.animate(
        [
          { transform: 'scale(1)', offset: 0 },
          { transform: 'scale(1.3)', offset: 0.28 },
          { transform: 'scale(0.92)', offset: 0.55 },
          { transform: 'scale(1.06)', offset: 0.78 },
          { transform: 'scale(1)', offset: 1 },
        ],
        { duration: 460, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
      );
    } else if (badge) {
      badge.remove();
    }
  }

  // ===== Empty the cart =====
  // Posts to /cart/clear.js (removes all line items). Used by "Buy Now" so the
  // express checkout contains only the product the shopper pressed Buy Now on.
  async function clearCart() {
    const res = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ===== AJAX add-to-cart =====
  // Posts FormData to /cart/add.js, refreshes the cart-bag badge from /cart.js,
  // and returns the add response. Throws on network / Shopify-validation failure.
  //
  // Pass `sectionId` to also get that section's freshly rendered HTML back in the
  // SAME response via Shopify's bundled section rendering (the cart/add `sections`
  // param — see https://shopify.dev/docs/api/ajax/reference/cart). POST responses
  // aren't cached, so this is the reliable way to refresh the cart drawer after an
  // add; a separate GET to `?sections=` can be served stale (empty cart) by the
  // browser/CDN, which left the drawer showing "empty" until a full reload.
  async function addToCart(formData, sectionId) {
    if (sectionId) {
      formData.append('sections', sectionId);
      formData.append('sections_url', window.location.pathname);
    }
    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/javascript' },
    });
    if (!addRes.ok) {
      const error = await addRes
        .json()
        .catch(() => ({ description: 'Unable to add item' }));
      throw new Error(error.description || `HTTP ${addRes.status}`);
    }
    const added = await addRes.json();
    await refreshCart();
    return added;
  }

  // ===== Refresh /cart.js, update header badge, return latest cart =====
  // Centralises the "get the new totals" step so cart-page.js, product-
  // page.js, and product-card.js all use the same shape. Emits a
  // `cart:updated` event so surfaces that mirror cart state (e.g. the cart
  // page, when an item is quick-added from the upsell rail) can re-render.
  async function refreshCart() {
    const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartRes.ok) return null;
    const cart = await cartRes.json();
    updateCartBadge(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  }

  // ===== Update a single cart line by line-item key =====
  // Posts to /cart/change.js. `qty: 0` removes the line. Returns the
  // refreshed cart (or throws on network / validation failure).
  //
  // Pass `sectionId` to also receive the freshly rendered HTML for that
  // section via the Section Rendering API (cart.sections[sectionId]) — this
  // keeps the Liquid cart section the single source of truth for totals and
  // line markup, so the caller can swap HTML instead of re-formatting money
  // in JS.
  async function changeCartLine(key, qty, sectionId) {
    const body = { id: key, quantity: qty };
    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const error = await res
        .json()
        .catch(() => ({ description: 'Unable to update cart' }));
      throw new Error(error.description || `HTTP ${res.status}`);
    }
    const cart = await res.json();
    updateCartBadge(cart.item_count);
    return cart;
  }

  // ===== Generic /cart/update.js (note, attributes, discounts) =====
  // Pass a payload such as { note: '…' }, { discount: 'CODE' }, or
  // { discount: '' } to clear. Pass sectionId to also receive the freshly
  // rendered section HTML under cart.sections[sectionId] (Section Rendering).
  async function updateCart(payload, sectionId) {
    const body = Object.assign({}, payload);
    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const error = await res
        .json()
        .catch(() => ({ description: 'Unable to update cart' }));
      throw new Error(error.description || `HTTP ${res.status}`);
    }
    const cart = await res.json();
    updateCartBadge(cart.item_count);
    return cart;
  }

  // ===== Button loading-state cycle =====
  // The CTA stays the same colour throughout (matches the mobile app's
  // ControlButton behaviour). Only the inner content cycles:
  //   idle → [spinner + label] → [success label] → idle.
  function spinnerHtml() {
    return '<span class="cart-action-spinner" aria-hidden="true"></span>';
  }

  function setBusy(btn, label) {
    if (!btn.dataset.originalHtml) {
      btn.dataset.originalHtml = btn.innerHTML;
    }
    btn.disabled = true;
    btn.innerHTML = `${spinnerHtml()}<span>${label}</span>`;
  }

  function setSuccess(btn, label, ms = ADDED_FEEDBACK_MS) {
    btn.innerHTML = `<span>${label}</span>`;
    setTimeout(() => restore(btn), ms);
  }

  function setError(btn, message, ms = ERROR_FEEDBACK_MS) {
    btn.innerHTML = `<span>${LABEL_RETRY}</span>`;
    if (message) btn.title = message;
    setTimeout(() => {
      restore(btn);
      btn.title = '';
    }, ms);
  }

  function restore(btn) {
    if (btn.dataset.originalHtml) {
      btn.innerHTML = btn.dataset.originalHtml;
      delete btn.dataset.originalHtml;
    }
    btn.disabled = false;
  }

  // ===== Bootstrap a button to do an AJAX add + cycle through states =====
  // Pass the button and a function that returns FormData when called
  // (deferred so quantity / variant changes are picked up at submit time).
  async function performAdd(btn, getFormData, { successLabel = LABEL_ADDED } = {}) {
    if (btn.disabled) return false;
    setBusy(btn, LABEL_ADDING);
    try {
      // Ask the add request to also render the cart-drawer section, so the drawer
      // refreshes from this (uncached) POST response instead of a cacheable GET.
      const drawerContents = document.querySelector('[data-cart-drawer] [data-cart-contents]');
      const drawerSectionId = drawerContents ? drawerContents.getAttribute('data-section-id') : null;
      const added = await addToCart(getFormData(), drawerSectionId);
      setSuccess(btn, successLabel);
      // Signal a successful add (vs a quantity change) so the cart drawer fills
      // in + opens, handing it the freshly rendered section HTML from the add.
      const sections = added && added.sections ? added.sections : null;
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { sections: sections } }));
      return true;
    } catch (error) {
      setError(btn, error.message);
      return false;
    }
  }

  window.toyCart = {
    formatMoney,
    spinnerHtml,
    renderSection,
    setLineBusy,
    updateCartBadge,
    clearCart,
    addToCart,
    refreshCart,
    changeCartLine,
    updateCart,
    setBusy,
    setSuccess,
    setError,
    restore,
    performAdd,
    labels: {
      adding: LABEL_ADDING,
      added: LABEL_ADDED,
      retry: LABEL_RETRY,
    },
  };
})();
